import { useState } from "react";

const createManifestRow = () => ({
  sample_id: "",
  forward_absolute_path: "",
  reverse_absolute_path: "",
});

const createMetadataRow = () => ({
  id: "",
  status: "",
});

const inputClassName = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]";

export const defaultValues = {
  manifest_file_rows: [],
  metadata_file_rows: [],
  select_column: "",
  select_file_type: "",
  select_database_type: "",
};

export const validate = (values = {}) => {
  const errors = {};

  if (!String(values.select_column || "").trim()) errors.select_column = "Select Column is required.";
  if (!String(values.select_file_type || "").trim()) errors.select_file_type = "Select File Type is required.";
  if (!String(values.select_database_type || "").trim()) errors.select_database_type = "Select Database Type is required.";

  const manifestRows = Array.isArray(values.manifest_file_rows) ? values.manifest_file_rows : [];
  if (manifestRows.length === 0) {
    errors.manifest_file_rows =
      "Please either fill the Manifest table manually or upload a Manifest template.";
  } else {
    manifestRows.forEach((row, index) => {
      if (!String(row?.sample_id || "").trim()) errors[`manifest_sample_id_${index}`] = "Sample ID is required.";
      if (!String(row?.forward_absolute_path || "").trim()) errors[`manifest_forward_${index}`] = "Forward absolute path is required.";
      if (!String(row?.reverse_absolute_path || "").trim()) errors[`manifest_reverse_${index}`] = "Reverse absolute path is required.";
    });
  }

  const metadataRows = Array.isArray(values.metadata_file_rows) ? values.metadata_file_rows : [];
  if (metadataRows.length === 0) {
    errors.metadata_file_rows =
      "Please either fill the Metadata table manually or upload a Metadata template.";
  } else {
    metadataRows.forEach((row, index) => {
      if (!String(row?.id || "").trim()) errors[`metadata_id_${index}`] = "ID is required.";
      if (!String(row?.status || "").trim()) errors[`metadata_status_${index}`] = "Status is required.";
    });
  }

  return errors;
};

export default function MetagenomicsParameters({
  values = {},
  onChange,
  errors = {},
  expectedSamples,
}) {
  const setValue = (key, value) => onChange?.(key, value);
  const [showManifestTable, setShowManifestTable] = useState(false);
  const [showMetadataTable, setShowMetadataTable] = useState(false);
  const [manifestSaved, setManifestSaved] = useState(false);
  const [metadataSaved, setMetadataSaved] = useState(false);
  const [manifestError, setManifestError] = useState("");
  const [metadataError, setMetadataError] = useState("");
  const [manifestRows, setManifestRows] = useState(
    Array.isArray(values.manifest_file_rows)
      ? values.manifest_file_rows
      : []
  );
  const [metadataRows, setMetadataRows] = useState(
    Array.isArray(values.metadata_file_rows)
      ? values.metadata_file_rows
      : []
  );

  const updateManifestRows = (nextRows) => {
    setManifestRows(nextRows);
    setValue("manifest_file_rows", nextRows);
  };

  const updateMetadataRows = (nextRows) => {
    setMetadataRows(nextRows);
    setValue("metadata_file_rows", nextRows);
  };

  const handleManifestRowChange = (index, field, value) => {
    const nextRows = manifestRows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row));
    updateManifestRows(nextRows);
  };

  const handleMetadataRowChange = (index, field, value) => {
    const nextRows = metadataRows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row));
    updateMetadataRows(nextRows);
  };

  const addManifestRow = () => {
    updateManifestRows([...manifestRows, createManifestRow()]);
  };

  const addMetadataRow = () => {
    updateMetadataRows([...metadataRows, createMetadataRow()]);
  };

  const removeManifestRow = (index) => {
    if (manifestRows.length === 1) return;
    const nextRows = manifestRows.filter((_, rowIndex) => rowIndex !== index);
    updateManifestRows(nextRows);
  };

  const removeMetadataRow = (index) => {
    if (metadataRows.length === 1) return;
    const nextRows = metadataRows.filter((_, rowIndex) => rowIndex !== index);
    updateMetadataRows(nextRows);
  };

  const downloadManifestTemplate = () => {
    const csv = [
      "Sample ID,Forward Absolute Path,Reverse Absolute Path",
      "Sample_1,/path/to/forward.fastq.gz,/path/to/reverse.fastq.gz",
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "manifest_template.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleManifestTemplateUpload = (file) => {

    if (showManifestTable && !manifestSaved && manifestRows.length > 0) {
      const confirmed = window.confirm(
        "Uploading a template will replace your current manual entries. Do you want to continue?"
      );

      if (!confirmed) return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;

      const rows = text
        .trim()
        .split("\n")
        .slice(1)
        .map((line) => {
          const [
            sample_id,
            forward_absolute_path,
            reverse_absolute_path,
          ] = line.split(",");

          return {
            sample_id: sample_id?.trim() || "",
            forward_absolute_path: forward_absolute_path?.trim() || "",
            reverse_absolute_path: reverse_absolute_path?.trim() || "",
          };
        });

      if (rows.length > 0) {
        updateManifestRows(rows);
        setShowManifestTable(true);
        setManifestSaved(true);
      }
    };

    reader.readAsText(file);
  };

  const downloadMetadataTemplate = () => {
    const csv = [
      "ID,Status",
      "Sample_1,Control",
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "metadata_template.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleMetadataTemplateUpload = (file) => {

    if (showMetadataTable && !metadataSaved && metadataRows.length > 0) {
      const confirmed = window.confirm(
        "Uploading a template will replace your current manual entries. Do you want to continue?"
      );

      if (!confirmed) return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;

      const rows = text
        .trim()
        .split("\n")
        .slice(1)
        .map((line) => {
          const [id, status] = line.split(",");

          return {
            id: id?.trim() || "",
            status: status?.trim() || "",
          };
        });

      if (rows.length > 0) {
        updateMetadataRows(rows);
        setShowMetadataTable(true);
        setMetadataSaved(true);
      }
    };

    reader.readAsText(file);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-[#09582D]">Manifest File</h3>
          <button
            type="button"
            onClick={() => {
              if (manifestRows.length === 0) {
                updateManifestRows([createManifestRow()]);
              }

              setShowManifestTable(true);
            }}
            className="rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3]"
          >
            Fill Manually
          </button>
          <button
            type="button"
            onClick={downloadManifestTemplate}
            className="rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3]"
          >
            Download Template
          </button>
          <label className="cursor-pointer rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3]">
            Upload Template

            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                handleManifestTemplateUpload(file);
              }}
            />
          </label>
        </div>
        {showManifestTable && (
          <div className="mt-4 space-y-3">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Sample ID</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Forward Absolute Path</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Reverse Absolute Path</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {manifestRows.map((row, index) => (
                    <tr key={`manifest-${index}`}>
                      <td className="px-3 py-2">
                        <input
                          value={row.sample_id || ""}
                          onChange={(e) => handleManifestRowChange(index, "sample_id", e.target.value)}
                          disabled={manifestSaved}
                          className={inputClassName}
                        />
                        {errors[`manifest_sample_id_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`manifest_sample_id_${index}`]}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={row.forward_absolute_path || ""}
                          onChange={(e) => handleManifestRowChange(index, "forward_absolute_path", e.target.value)}
                          disabled={manifestSaved}
                          className={inputClassName}
                        />
                        {errors[`manifest_forward_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`manifest_forward_${index}`]}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={row.reverse_absolute_path || ""}
                          onChange={(e) => handleManifestRowChange(index, "reverse_absolute_path", e.target.value)}
                          disabled={manifestSaved}
                          className={inputClassName}
                        />
                        {errors[`manifest_reverse_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`manifest_reverse_${index}`]}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeManifestRow(index)}
                          disabled={manifestSaved || manifestRows.length === 1}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Remove Row
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.manifest_file_rows && <p className="text-xs text-red-600">{errors.manifest_file_rows}</p>}
            {manifestError && (
              <p className="text-sm text-red-600">
                {manifestError}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={addManifestRow}
                disabled={manifestSaved}
                className="rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add Row
              </button>
              {manifestSaved ? (
                <button
                  type="button"
                  onClick={() => setManifestSaved(false)}
                  className="rounded-md bg-[#538135] px-3 py-2 text-sm font-semibold text-white hover:bg-[#09582D]"
                >
                  Modify
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const expected = Number(expectedSamples || 0);

                    if (expected > 0 && manifestRows.length !== expected) {
                      setManifestError(
                        `You specified ${expected} sample${expected > 1 ? "s" : ""}, but ${manifestRows.length} manifest row${manifestRows.length !== 1 ? "s have" : " has"} been added.`
                      );
                      return;
                    }
                    setManifestError("");
                    setManifestSaved(true);
                  }}
                  className="rounded-md bg-[#538135] px-3 py-2 text-sm font-semibold text-white hover:bg-[#09582D]"
                >
                  Save
                </button>
              )}
              {!manifestSaved && (
                <button
                  type="button"
                  onClick={() => {
                    setShowManifestTable(false);
                    setManifestRows([]);
                    updateManifestRows([]);
                  }}
                  className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-[#09582D]">Metadata File</h3>
          <button
            type="button"
            onClick={() => {
              if (metadataRows.length === 0) {
                updateMetadataRows([createMetadataRow()]);
              }

              setShowMetadataTable(true);
            }}
            className="rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3]"
          >
            Fill Manually
          </button>
          <button
            type="button"
            onClick={downloadMetadataTemplate}
            className="rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3]"
          >
            Download Template
          </button>

          <label className="cursor-pointer rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3]">
            Upload Template

            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                handleMetadataTemplateUpload(file);
              }}
            />
          </label>
        </div>
        {showMetadataTable && (
          <div className="mt-4 space-y-3">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">ID</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {metadataRows.map((row, index) => (
                    <tr key={`metadata-${index}`}>
                      <td className="px-3 py-2">
                        <input
                          value={row.id || ""}
                          onChange={(e) => handleMetadataRowChange(index, "id", e.target.value)}
                          disabled={metadataSaved}
                          className={inputClassName}
                        />
                        {errors[`metadata_id_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`metadata_id_${index}`]}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={row.status || ""}
                          onChange={(e) => handleMetadataRowChange(index, "status", e.target.value)}
                          disabled={metadataSaved}
                          className={inputClassName}
                        />
                        {errors[`metadata_status_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`metadata_status_${index}`]}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeMetadataRow(index)}
                          disabled={metadataSaved || metadataRows.length === 1}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Remove Row
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.metadata_file_rows && <p className="text-xs text-red-600">{errors.metadata_file_rows}</p>}
            {metadataError && (
              <p className="text-sm text-red-600">
                {metadataError}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={addMetadataRow}
                disabled={metadataSaved}
                className="rounded-md border border-[#538135] bg-white px-3 py-2 text-sm font-semibold text-[#09582D] hover:bg-[#EAF4E3] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add Row
              </button>
              {metadataSaved ? (
                <button
                  type="button"
                  onClick={() => setMetadataSaved(false)}
                  className="rounded-md bg-[#538135] px-3 py-2 text-sm font-semibold text-white hover:bg-[#09582D]"
                >
                  Modify
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const expected = Number(expectedSamples || 0);

                    if (expected > 0 && metadataRows.length !== expected) {
                      setMetadataError(
                        `You specified ${expected} sample${expected > 1 ? "s" : ""}, but ${metadataRows.length} metadata row${metadataRows.length !== 1 ? "s have" : " has"} been added.`
                      );
                      return;
                    }

                    setMetadataError("");
                    setMetadataSaved(true);
                  }}
                  className="rounded-md bg-[#538135] px-3 py-2 text-sm font-semibold text-white hover:bg-[#09582D]"
                >
                  Save
                </button>
              )}
              {!metadataSaved && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMetadataTable(false);
                    setMetadataRows([]);
                    updateMetadataRows([]);
                  }}
                  className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Select Column</label>
            <select value={values.select_column || ""} onChange={(e) => setValue("select_column", e.target.value)} className={inputClassName}>
              <option value="">Select</option>
              <option value="ID">ID</option>
              <option value="Status">Status</option>
            </select>
            {errors.select_column && <p className="mt-1 text-xs text-red-600">{errors.select_column}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Select File Type</label>
            <select value={values.select_file_type || ""} onChange={(e) => setValue("select_file_type", e.target.value)} className={inputClassName}>
              <option value="">Select</option>
              <option value="Raw File">Raw File</option>
              <option value="Filter File">Filter File</option>
            </select>
            {errors.select_file_type && <p className="mt-1 text-xs text-red-600">{errors.select_file_type}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Select Database Type</label>
            <select value={values.select_database_type || ""} onChange={(e) => setValue("select_database_type", e.target.value)} className={inputClassName}>
              <option value="">Select</option>
              <option value="GreenGenes">GreenGenes</option>
              <option value="SILVA">SILVA</option>
              <option value="Unite">Unite</option>
            </select>
            {errors.select_database_type && <p className="mt-1 text-xs text-red-600">{errors.select_database_type}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
